import React from "react";
import { Activity, Bell, ExternalLink, TrendingDown, TrendingUp } from "lucide-react";
import type { CryptoAlertData } from "@crypto-trading/common/browser";
import { TokenIcon } from "@/components/TokenIcon";
import { AlertFunding } from "@/components/AlertFunding";

interface AlertDetailSummaryProps {
  alert: CryptoAlertData;
  livePrice: number | null | undefined;
  isSubscribed: boolean;
  isPriceLoading: boolean;
  priceChange: number | null;
  formatPrice: (price: number | null) => string;
  formatDateTime: (date: Date) => string;
  /** The user's connected exchanges, first one picked for funding. */
  connectedExchangeCodes: string[];
  /** Funding is fetched only while the sheet is open. */
  open: boolean;
}

/** Token line of the alert details (logo, symbol, direction, badges). */
export const AlertDetailTitle: React.FC<{ alert: CryptoAlertData }> = ({ alert }) => {
  const isListing = alert.type === "new_coin";
  return (
    <div className="flex items-center gap-2">
      <TokenIcon symbol={alert.currency} logo={alert.tokenMetadata?.logo} size="md" />
      <h2 className="text-base font-semibold text-white">{alert.currency}</h2>
      {isListing ? (
        <TrendingUp className="h-4 w-4 text-[#4ade80]" />
      ) : (
        <TrendingDown className="h-4 w-4 text-[#f87171]" />
      )}
      <div className="flex flex-wrap gap-0.5">
        <span className="rounded-full border border-[#404040] bg-[#171717] px-2 text-[10px] font-medium leading-4 text-[#d4d4d4]">
          {isListing ? "Listing" : "Delisting"}
        </span>
        {alert.hasAutotraded && (
          <span className="rounded-full border border-[#00653b] bg-[#002c14] px-2 text-[10px] font-medium leading-4 text-[#56d799]">
            Autotraded 🤖
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Alert details summary from the redesign, shared by the mobile sheet and the
 * desktop side sheet: where and when, alert vs current price, funding.
 */
export const AlertDetailSummary: React.FC<AlertDetailSummaryProps> = ({
  alert,
  livePrice,
  isSubscribed,
  isPriceLoading,
  priceChange,
  formatPrice,
  formatDateTime,
  connectedExchangeCodes,
  open,
}) => (
  <>
    {/* Where and when */}
    <div className="edge-glow flex items-center justify-between gap-2 rounded-xl border border-[rgba(7,166,108,0.4)] bg-[rgba(13,18,26,0.5)] px-3 py-2 backdrop-blur-[6px]">
      <div className="min-w-0">
        <p className="text-[10px] leading-3 text-[#a3a3a3]">Exchange</p>
        <p className="text-xs font-semibold capitalize leading-[18px] text-white">{alert.exchange}</p>
      </div>
      <div className="h-[34px] w-px bg-[#262626]" />
      <div className="min-w-0">
        <p className="text-[10px] leading-3 text-[#a3a3a3]">Exchange Type</p>
        <p className="text-xs font-semibold capitalize leading-[18px] text-white">
          {alert.exchangeType || "spot"}
        </p>
      </div>
      <div className="h-[34px] w-px bg-[#262626]" />
      <div className="min-w-0">
        <p className="text-[10px] leading-3 text-[#a3a3a3]">Alert Time</p>
        <p className="text-xs font-semibold leading-[18px] text-white">
          {formatDateTime(alert.createdAt)}
        </p>
      </div>
      {alert.source ? (
        <a
          href={alert.source}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Source"
          className="shrink-0 text-[#4ade80]"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
      ) : (
        <span className="w-4" />
      )}
    </div>

    {/* Alert price | current price */}
    <div className="flex items-stretch gap-4">
      <div className="glass flex flex-1 items-start gap-1.5 px-3 py-1.5">
        <span className="mt-1.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-[#525252]">
          <Bell className="size-4 text-white" />
        </span>
        <div className="min-w-0">
          <p className="text-xs leading-[18px] text-[#a3a3a3]">Alert Price</p>
          <p className="text-sm font-semibold leading-5 text-white">{formatPrice(alert.price)}</p>
        </div>
      </div>
      <div className="edge-glow flex flex-1 items-start gap-1.5 overflow-hidden rounded-xl border border-white/10 bg-[rgba(22,163,74,0.1)] px-3 py-1.5 backdrop-blur-[4px]">
        <span className="mt-1.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-[#16a34a]">
          <Activity className="size-4 text-white" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <p className="text-xs leading-[18px] text-[#a3a3a3]">Current Price</p>
            {isSubscribed && livePrice && (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
              </span>
            )}
          </div>
          {isPriceLoading ? (
            <div className="mt-0.5 h-5 w-20 animate-pulse rounded bg-gray-600/30"></div>
          ) : (
            <>
              <p className="text-sm font-semibold leading-5 text-white">{formatPrice(livePrice ?? null)}</p>
              {priceChange !== null && (
                <p
                  className={`text-[10px] font-semibold leading-3 ${priceChange >= 0 ? "text-[#4ade80]" : "text-[#f87171]"}`}
                >
                  {priceChange >= 0 ? "+" : ""}
                  {priceChange.toFixed(2)}%
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>

    {/* Funding, switchable between the exchanges listing the token */}
    <div className="glass p-3">
      <AlertFunding symbol={alert.currency} preferred={connectedExchangeCodes} enabled={open} />
    </div>
  </>
);
